/**
 * Terminal data types and interfaces
 * Replaces the old Device terminology with Terminal
 */

export type TerminalStatus = 'unscanned' | 'clean' | 'possible_risk' | 'under_investigation';

export type TerminalSensitivity = 'low' | 'medium' | 'high';

export interface TerminalLocation {
  description: string;
  coordinates?: {
    x: number;
    y: number;
  };
}

export interface ScanDefaults {
  duration: number; // in seconds
  sensitivity: TerminalSensitivity;
}

export interface Terminal {
  id: string;
  name: string; // Display name for the terminal
  terminalType: string; // e.g., "ATM", "Card Reader", "POS Terminal"
  makeModel: string; // e.g., "NCR SelfServ 80", "Ingenico iCT250"
  readerModel?: string; // Specific card reader model if different
  serialNumber: string;
  siteLocation: TerminalLocation;
  assignedUnitId?: string; // Reference to SkimBuster hardware unit
  tags: string[]; // Flexible tagging system
  lastScannedAt?: Date;
  scanDefaults: ScanDefaults;
  status: TerminalStatus;
  
  // Metadata
  siteId: string;
  masterOwnerID: string;
  createdAt: Date;
  updatedAt: Date;
}

// Backwards compatibility interfaces - matches actual Firebase structure
export interface LegacyDevice {
  id: string;
  modelName?: string;
  deviceType?: string;
  modelNumber?: string;
  specification?: string;
  qrCodeData?: string;
  qrcode?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  batteryLevel?: number;
  wifiSignal?: number;
  firmwareVersion?: string;
  isActive?: boolean;
  lastSeen?: Date | any; // Firebase timestamp or Date
  siteId?: string;
  siteName?: string;
  masterOwnerID?: string;
  createdAt?: Date | any; // Firebase timestamp or Date
  updatedAt?: Date | any; // Firebase timestamp or Date
  assignedUnitId?: string;
  tags?: string[];
  // Allow additional properties for web app terminal data
  terminalData?: {
    name?: string;
    serialNumber?: string;
    tags?: string[];
    scanDefaults?: {
      duration: number;
      sensitivity: 'low' | 'medium' | 'high';
    };
    status?: TerminalStatus;
    assignedUnitId?: string;
  };
  // Legacy fields for backward compatibility
  status?: 'online' | 'offline' | 'maintenance' | 'error';
  location?: {
    description: string;
    coordinates?: {
      x: number;
      y: number;
    };
  };
  configuration?: {
    scanInterval: number;
    sensitivity: 'low' | 'medium' | 'high';
    autoUpdate: boolean;
    alertsEnabled: boolean;
  };
}

// Hardware unit interface (for the actual SkimBuster devices)
export interface HardwareUnit {
  id: string;
  unitId: string; // Physical device identifier
  modelName: string; // e.g., "SkimBuster V1", "SkimBuster Pro"
  serialNumber: string;
  firmwareVersion: string;
  batteryLevel: number;
  signalStrength: number;
  connectionStatus: 'online' | 'offline' | 'maintenance' | 'error';
  lastSeen: Date;
  assignedTerminalId?: string;
  configuration: {
    scanInterval: number;
    autoUpdate: boolean;
    alertsEnabled: boolean;
  };
  createdAt: Date;
  updatedAt: Date;
}

// Migration helper functions
export const mapLegacyDeviceToTerminal = (device: LegacyDevice): Terminal => {
  // Check if this device has terminal-specific data saved from web app
  const terminalData = (device as any).terminalData;
  
  return {
    id: device.id,
    name: terminalData?.name || device.specification || device.modelName || 'Unnamed Terminal',
    terminalType: device.deviceType || 'Card Reader',
    makeModel: device.modelName || 'Unknown',
    readerModel: device.modelNumber || '',
    serialNumber: terminalData?.serialNumber || device.qrCodeData || device.qrcode || '',
    siteLocation: {
      description: device.specification || '',
      coordinates: device.coordinates ? {
        x: device.coordinates.longitude,
        y: device.coordinates.latitude,
      } : undefined,
    },
    tags: terminalData?.tags || [],
    lastScannedAt: device.lastSeen ? new Date(device.lastSeen) : undefined,
    scanDefaults: terminalData?.scanDefaults || {
      duration: 30,
      sensitivity: 'medium',
    },
    status: terminalData?.status || mapDeviceActiveStatusToTerminalStatus(device.isActive),
    siteId: device.siteId || '',
    masterOwnerID: device.masterOwnerID || '',
    createdAt: device.createdAt ? new Date(device.createdAt) : new Date(),
    updatedAt: device.updatedAt ? new Date(device.updatedAt) : new Date(),
    assignedUnitId: terminalData?.assignedUnitId || '',
  };
};

export const mapLegacyStatusToTerminalStatus = (legacyStatus: LegacyDevice['status']): TerminalStatus => {
  switch (legacyStatus) {
    case 'online':
      return 'clean';
    case 'maintenance':
      return 'under_investigation';
    case 'error':
      return 'possible_risk';
    case 'offline':
    default:
      return 'unscanned';
  }
};

export const mapTerminalStatusToLegacyStatus = (status: TerminalStatus): LegacyDevice['status'] => {
  switch (status) {
    case 'clean':
      return 'online';
    case 'under_investigation':
      return 'maintenance';
    case 'possible_risk':
      return 'error';
    case 'unscanned':
    default:
      return 'offline';
  }
};

// Map device active status (from Firebase structure) to terminal status
export const mapDeviceActiveStatusToTerminalStatus = (isActive?: boolean): TerminalStatus => {
  return isActive ? 'clean' : 'unscanned';
};

// Status display helpers
export const getTerminalStatusColor = (status: TerminalStatus): string => {
  switch (status) {
    case 'clean':
      return '#4caf50'; // Green
    case 'unscanned':
      return '#757575'; // Gray
    case 'possible_risk':
      return '#ff9800'; // Orange/Yellow
    case 'under_investigation':
      return '#f44336'; // Red
    default:
      return '#757575';
  }
};

export const getTerminalStatusLabel = (status: TerminalStatus): string => {
  switch (status) {
    case 'clean':
      return 'Clean';
    case 'unscanned':
      return 'Unscanned';
    case 'possible_risk':
      return 'Possible Risk';
    case 'under_investigation':
      return 'Under Investigation';
    default:
      return 'Unknown';
  }
};
